/**
 * Pre-built conversation starters with per-card heuristic matching.
 *
 * Each card has a `match` function that evaluates against the workspace context
 * to determine relevance. Cards whose heuristics reference count data (populated
 * in Phase 2 from existing endpoints) return true when counts are null, entering
 * the daily shuffle pool alongside fully-matched cards.
 */

import { isEmptyString } from '@/utils/string';
import type { WorkspaceContext } from './workspace-context';

export interface PromptCard {
  title: string;
  description: string;
  prompt: string;
  /** Returns true if this card is relevant for the given workspace. */
  match: (context: WorkspaceContext) => boolean;
}

export const prompts: PromptCard[] = [
  // ── Getting Started / Setup ─────────────────────────────────────────

  {
    title: 'Connect your data source',
    description: 'Pick the right integration method for your tech stack and get it configured.',
    prompt:
      "I need to get my customer data flowing into Customer.io and I'm not sure which integration method to use. Ask me about my tech stack — whether I have a backend, a frontend web app, a mobile app, or if I'm working with existing tools like Segment. Based on my setup, recommend the best integration method and walk me through configuring it. Explain any credentials or code I'll need and flag any common pitfalls.",
    match: (context) => context.integrationCompletedAt == null,
  },
  {
    title: 'Import your contacts',
    description: 'Bring your existing audience in via CSV and get them ready to message.',
    prompt:
      'I want to import my existing contacts into Customer.io via CSV. Walk me through how to prepare my file — what columns I need, how to format attributes, and any gotchas to avoid. Then explain what happens after the import: how my contacts will appear, how to verify it worked, and what I should set up next to start messaging them. Ask me about my data first so you can tailor the guidance.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount === 0;
    },
  },
  {
    title: 'Set up email sending',
    description: 'Verify your domain, add a from address, and get out of test mode.',
    prompt:
      "I need to set up email sending in Customer.io so I can send real emails. Walk me through the full process: adding and verifying my sending domain, configuring the DNS records I'll need (MX, SPF, DKIM, DMARC), adding a from address, submitting my account for verification, and switching out of test delivery mode. Explain what each step does and why it matters. Ask me what domain I'm planning to use before we start.",
    match: (context) => context.deliverySettings !== 'normal',
  },
  {
    title: 'Figure out what to track',
    description: 'Know which events and attributes will unlock better campaigns.',
    prompt:
      "I want to make sure I'm tracking the right customer data in Customer.io. Ask me about my product — what it does, who uses it, and what actions matter most. Then recommend the key events and attributes I should be tracking to unlock better segmentation, smarter campaign triggers, and personalization. Explain what each one would enable so I understand the value of instrumenting it.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.eventsCount < 5;
    },
  },
  {
    title: 'Design your first email template',
    description: 'Build a reusable email in Design Studio that matches your brand.',
    prompt:
      "I want to design my first email template in Design Studio. Before we start, ask me about my brand — colors, fonts, logo, and the general tone I'm going for. Then walk me through building a clean, reusable template: setting up a header and footer, applying my brand styles, and structuring the content area so it's easy to reuse across different campaigns. Suggest what to set as global styles so I don't have to repeat myself in every template.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount < 2;
    },
  },

  // ── First Campaigns & Messages ──────────────────────────────────────

  {
    title: 'Build my first campaign',
    description: 'Choose a trigger, write a message, and get it live.',
    prompt:
      "I've never built a campaign before and I want to create my first one. Start by asking me what I'm trying to accomplish — like welcoming new signups, following up after a purchase, or re-engaging someone who went quiet. Based on my answer, recommend the right trigger type and explain why. Then walk me through building it step by step: setting up the trigger, writing the message, adding any delays or conditions that make sense, and activating it. Explain each decision as we go so I understand what I'm building and why.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount === 0;
    },
  },
  {
    title: 'Create a welcome series',
    description: 'Greet new subscribers with a sequence that makes a great first impression.',
    prompt:
      "I want to create a welcome email series for new subscribers. Before you suggest anything, ask me a few questions: What does my product or service do? What do I want a new subscriber to do in their first week? Do I have any existing content or emails I'm working from? Then recommend a 3–4 email sequence with a theme and goal for each message, suggested timing between sends, and guidance on setting it up as a campaign in Customer.io.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount < 3;
    },
  },
  {
    title: 'Create my first segment',
    description: 'Group your audience so you can message the right people.',
    prompt:
      "I want to create my first segment but I'm not sure where to start. Explain the difference between data-driven and manual segments in plain terms. Then ask me what I'm trying to do — like targeting people who signed up recently, haven't engaged, or have a specific attribute — and help me build the right conditions for that goal. Walk me through each condition I'm adding so I understand what it does and how to adjust it later.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.segmentsCount < 2;
    },
  },
  {
    title: 'Send my first broadcast',
    description: 'One-time send to a segment — scheduled or right now.',
    prompt:
      'I want to send a broadcast to my audience. Ask me what I want to send and who I want to send it to. Help me identify or create the right segment for the audience, write or refine the message, and walk me through the broadcast setup — subject line, from address, scheduling vs. sending now, and anything I should review before hitting send. Flag anything that looks off before I go live.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.newslettersCount === 0;
    },
  },

  // ── Channel Setup ───────────────────────────────────────────────────

  {
    title: 'Set up push notifications',
    description: 'Configure push providers and send your first mobile message.',
    prompt:
      "I want to set up push notifications in Customer.io. Ask me which platforms I need to support — iOS, Android, or both — and what push provider I'm using (APNs, FCM). Walk me through adding the credentials, configuring the provider in Customer.io, and setting up my first push campaign. Explain what I need from my mobile app to receive pushes and how to verify everything is connected.",
    match: (context) => context.integrationCompletedAt != null && !context.enabledActions.includes('push'),
  },
  {
    title: 'Set up SMS messaging',
    description: 'Connect Twilio and send your first text message campaign.',
    prompt:
      'I want to start sending SMS messages through Customer.io. Walk me through connecting my Twilio account, choosing the right phone number type, configuring the integration, and building my first SMS campaign. Ask me about my use case — transactional alerts, marketing messages, or both — so you can recommend the right setup. Flag any compliance or opt-in requirements I should know about.',
    match: (context) => context.integrationCompletedAt != null && !context.enabledActions.includes('twilio'),
  },
  {
    title: 'Set up in-app messages',
    description: 'Show targeted messages inside your app or website.',
    prompt:
      "I want to set up in-app messaging so I can show messages directly inside my product. Ask me whether I'm building for web, mobile, or both. Walk me through installing the SDK, configuring it to receive messages, and creating my first in-app message campaign. Help me think about when and where to show messages so they feel helpful, not intrusive.",
    match: (context) => context.integrationCompletedAt != null && !context.enabledActions.includes('in_app'),
  },
  {
    title: 'Send your first push campaign',
    description: 'Create and launch a push notification to engage mobile users.',
    prompt:
      "I have push notifications configured and I want to create my first push campaign. Ask me what I'm trying to accomplish — onboarding nudge, feature announcement, re-engagement, or something else. Help me write a compelling push message (title and body), set up the right trigger, define the audience, and configure any deep links. Walk me through testing it before going live.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.enabledActions.includes('push') && context.counts.campaignsCount >= 1;
    },
  },
  {
    title: 'Set up WhatsApp messaging',
    description: 'Connect your WhatsApp Business account and send your first message.',
    prompt:
      'I want to start messaging customers on WhatsApp through Customer.io. Walk me through connecting my WhatsApp Business account, getting approved message templates, and creating my first WhatsApp campaign. Ask me about my use case and audience so you can recommend the right approach. Flag any approval requirements or limitations I should know about.',
    match: (context) => context.integrationCompletedAt != null && !context.enabledActions.includes('whatsapp'),
  },

  // ── Growing / Leveling Up ──────────────────────────────────────────

  {
    title: 'Build a gap-based campaign',
    description: "Find a campaign type you're missing and build it.",
    prompt:
      "I've already built some campaigns but I want to make sure I'm not missing anything important. Review the types of campaigns I have set up and identify a meaningful gap — a trigger type, lifecycle moment, or use case I haven't covered yet. Explain why it matters and what I'm leaving on the table without it. Then help me build it: walk me through the trigger, the message, and any conditions or timing that would make it effective.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Make your welcome series smarter',
    description: 'Add behavioral branching so it responds to what new users actually do.',
    prompt:
      "I have a welcome series running but it treats everyone the same way. I want to make it smarter by branching based on what new users actually do. Ask me what actions I'd consider a signal of engagement in the first few days. Then help me add conditional logic to my existing series — different paths for people who engage vs. those who don't, wait-for-event steps, and exits for people who complete the goal early. Walk me through the changes without rebuilding from scratch.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Build a high-value segment',
    description: 'Find your most engaged users using event frequency and recency.',
    prompt:
      "I want to build a segment of my most engaged users so I can treat them differently — whether that's a loyalty campaign, early access, or just understanding who my best customers are. Ask me what engagement looks like in my product: which events signal real usage, how often they should be doing them, and over what time window. Then help me build a segment using event frequency and recency conditions, and suggest what I should do with this audience once I have it.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.segmentsCount >= 2;
    },
  },
  {
    title: 'Win back quiet contacts',
    description: "Create a campaign for people who've gone silent.",
    prompt:
      'I want to re-engage contacts who have gone quiet. Help me define what "inactive" means for my audience — ask me what events signal engagement and how long a gap would concern me. Then help me build a segment of those inactive contacts and a re-engagement campaign with a compelling message. Walk me through adding a sunset path: what to do with people who still don\'t respond so I\'m not hurting my deliverability.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 1;
    },
  },
  {
    title: 'Plan a content calendar',
    description: 'Turn one-off broadcasts into a consistent sending rhythm.',
    prompt:
      "I've been sending broadcasts occasionally but I want to be more intentional about it. Ask me about my audience, the types of content I have to share, and how often I want to be in their inbox. Then help me map out a realistic broadcast calendar — recurring content themes, suggested cadence, and how to balance promotional sends with value-driven ones. Give me something concrete I can actually follow, not just a framework.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.newslettersCount >= 2;
    },
  },
  {
    title: 'Run your first A/B test',
    description: 'Test a subject line or content variation to find what performs better.',
    prompt:
      "I want to run my first A/B test but I'm not sure what to test or how to set it up. Ask me which campaign or broadcast I want to experiment with and what my goal is — opens, clicks, conversions. Help me pick a meaningful thing to test (subject line, content, CTA, timing) and explain how to set up the split in Customer.io. Tell me how long to run it, how to read the results, and what to do with the winner.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Set up a transactional message',
    description: 'Send order confirmations, password resets, or receipts through Customer.io.',
    prompt:
      "I want to set up a transactional message for a key moment in my product — like a confirmation, receipt, or alert. Ask me what I'm trying to send and what triggers it. Explain the difference between transactional messages and campaign-based sends, and help me decide which approach fits my use case. Then walk me through building it, integrating the API call from my app, and testing that it delivers correctly.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.transactionalCount === 0 && context.counts.campaignsCount >= 1;
    },
  },
  {
    title: 'Enrich your customer profiles',
    description: 'Find the gaps in your data that are limiting your targeting.',
    prompt:
      "I want to make sure I'm getting the most out of my customer data. Look at the events and attributes I'm currently tracking and tell me what's missing or underused. Ask me about my product and what I want to personalize or trigger off of. Then suggest specific data points I should start collecting — events to instrument, attributes to pass, or relationships to model — and explain what each one would unlock in terms of segmentation and campaign triggers.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 1;
    },
  },
  {
    title: 'Build a date-triggered campaign',
    description: 'Send messages based on birthdays, renewals, or any date attribute.',
    prompt:
      "I want to build a campaign that triggers based on a date — like a renewal reminder, anniversary message, or birthday email. Ask me which date attribute I want to use and what I'm trying to accomplish. Then help me set up the trigger with the right offset (before or after the date), write a message that fits the moment, and think through edge cases like missing data or time zones. Suggest variations I could test to improve performance.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 1 && context.counts.customerAttributesCount >= 3;
    },
  },
  {
    title: 'Set up a Goal to track conversions',
    description: 'Define what success looks like and tie it to your campaigns.',
    prompt:
      'I want to start tracking conversions properly using Goals in Customer.io. Ask me what business outcome I care most about — like a purchase, a trial conversion, or a feature activation. Help me define the goal event, set up any milestones or funnel steps that lead to it, assign a value if applicable, and attach it to the right campaigns. Then explain how to read the attribution data once it starts coming in.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Model your data with Custom Objects',
    description: 'Structure complex data like accounts or orders and trigger off of it.',
    prompt:
      "I think I need Custom Objects to properly model my data but I'm not sure where to start. Ask me about my business — what entities matter beyond individual people, like companies, accounts, orders, or properties. Help me design the right object types and relationship structure, explain what campaigns and triggers it unlocks, and walk me through creating my first object type. Flag anything I should think through before I commit to a data model.",
    match: (context) => context.objectTypesCount === 0,
  },

  // ── Established / Optimization ──────────────────────────────────────

  {
    title: 'Improve an underperforming campaign',
    description: "Pick one and find out what's dragging it down.",
    prompt:
      "I have a campaign that isn't performing as well as I'd expect and I want to figure out why. Ask me which campaign I'm concerned about — or help me identify one worth looking at based on what you know about my account. Then dig into it with me: look at the trigger logic, the message content, the timing, the audience conditions, and the exit criteria. Give me a specific diagnosis, not a generic checklist, and tell me what to change first and why.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },
  {
    title: 'Audit your onboarding flow',
    description: 'Find where new users drop off and what to do about it.',
    prompt:
      "I want to audit my onboarding experience end-to-end. Walk me through reviewing every campaign, message, and touchpoint a new user encounters in their first 30 days. Help me identify where engagement drops off, which messages might be landing at the wrong time, and what's missing entirely. Then give me a prioritized list of improvements — what to fix, what to add, and what to cut. Ask me what a successful onboarding looks like before we start.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },
  {
    title: 'Audit my segment strategy',
    description: 'Find overlaps, gaps, and cleanup opportunities.',
    prompt:
      "I want a full audit of my segment setup. Help me identify segments that overlap significantly, conditions that are outdated or too broad, and use cases I'm not covering that I should be. Look for segments that aren't attached to any campaign and ones that could be consolidated. Give me a clean list of what to fix, what to delete, and what new segments would meaningfully improve my targeting.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.segmentsCount >= 5;
    },
  },
  {
    title: 'Audit your template library',
    description: 'Check your emails for consistency, accessibility, and opportunities to consolidate.',
    prompt:
      'I want to audit my email templates across campaigns and broadcasts. Help me identify inconsistencies in branding, formatting, or structure — things like mismatched fonts, inconsistent CTAs, or layouts that have drifted from my brand standards. Flag any accessibility issues like missing alt text or low-contrast text. Identify templates that are doing the same job and could be consolidated into a single reusable layout. Give me a prioritized list of what to fix.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },
  {
    title: 'Build a multi-touch win-back flow',
    description: 'Escalating touchpoints, behavioral exits, and list hygiene built in.',
    prompt:
      'I want to build a sophisticated re-engagement workflow — not just a single win-back email. Help me design a multi-step flow with escalating touchpoints: a soft reminder, a stronger nudge, and a final incentive or farewell. Build in behavioral exits so people who re-engage leave the flow immediately. End with an automatic sunsetting step for contacts who never respond so I\'m protecting my sender reputation. Ask me about my audience and what "inactive" means before we design the flow.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },
  {
    title: 'Analyze my broadcast performance',
    description: "Find what's working across your newsletters and what to change.",
    prompt:
      "I want to take a hard look at my broadcast performance across all my newsletters. Help me identify patterns in what's working — subject line styles, content types, send timing, audience segments — and what's underperforming. Compare my best and worst sends and tell me what's different about them. Then give me 3–5 specific, actionable changes I should make to my next broadcast based on what the data suggests.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.newslettersCount >= 5;
    },
  },
  {
    title: 'Design a meaningful experiment',
    description: 'Go beyond subject lines — test content strategy, timing, or full variants.',
    prompt:
      "I want to run a more sophisticated experiment than a simple subject line test. Ask me what I'm trying to learn and what outcome I'm optimizing for. Help me define a clear hypothesis, choose what to test — content strategy, message timing, audience segmentation, or full creative variants — and structure the test so the results will actually be statistically meaningful. Tell me how long to run it, how to avoid common mistakes, and how to apply what I learn.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },
  {
    title: 'Audit your transactional messages',
    description: 'Make sure your receipts and alerts are doing more than the minimum.',
    prompt:
      "I want to audit all my transactional messages — confirmations, receipts, alerts, password resets. For each one, help me evaluate whether it's well-written, on-brand, and structured clearly. Identify ones that are purely functional but could add more value with a relevant recommendation, next step, or piece of content. Flag any that look outdated or inconsistent with my current brand. Give me a prioritized list of improvements ranked by impact.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.transactionalCount >= 2;
    },
  },
  {
    title: 'Review your deliverability health',
    description: 'Check your domain setup and sending practices for inbox placement risks.',
    prompt:
      'I want a full deliverability review. Start with my domain configuration — SPF, DKIM, DMARC — and tell me if anything is missing or misconfigured. Then look at my sending practices: list hygiene, engagement rates, unsubscribe handling, and sending volume patterns. Identify anything that could be hurting my inbox placement or putting my sender reputation at risk. Give me a prioritized action plan with the highest-impact fixes first.',
    match: (context) => context.deliverySettings === 'normal',
  },
  {
    title: 'Build an API-triggered broadcast',
    description: 'Create a workflow your app can trigger programmatically with custom data.',
    prompt:
      "I want to set up an API-triggered broadcast that my app can fire programmatically. Ask me what event or action in my product should trigger it and what custom data I'll need to pass through. Then walk me through building the workflow — setting up the trigger, mapping the data fields, writing the message with liquid personalization, and testing it end-to-end. Help me think through error handling and what to monitor once it's live.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Turn a goal into a campaign plan',
    description: 'Start with the outcome you want — the agent maps out what to build.',
    prompt:
      "I want to work backwards from a business goal to a campaign plan. Ask me what outcome I'm trying to drive — like improving trial-to-paid conversion, reducing churn, increasing feature adoption, or growing revenue from existing customers. Then map out the campaigns, segments, triggers, and messages I should build to move that metric. Prioritize them by expected impact and tell me what to build first, what data I'll need, and how I'll know if it's working.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },

  // ── Advanced / Deep ─────────────────────────────────────────────────

  {
    title: "Find the revenue I'm leaving on the table",
    description: "Identify high-value segments that aren't enrolled in any campaign.",
    prompt:
      "I want to find revenue opportunities I'm not currently acting on. Start by helping me define what \"high-value\" looks like in my account — ask me about purchase behavior, usage signals, plan type, or any attributes that indicate a contact is worth more than average. Then cross-reference those characteristics against my active campaigns to find high-value contacts or segments that aren't enrolled in anything — or that are only in generic campaigns that don't reflect their value. For each gap you find, suggest a specific campaign or message that would address it and give me a rough sense of the opportunity. I want to come out of this with a prioritized list of things to build, ordered by expected impact.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },
  {
    title: 'Map my full customer journey',
    description: 'See every message a user receives from day 1 through 90 days — gaps, overlaps, and all.',
    prompt:
      "I want a complete picture of what a new customer actually experiences from the moment they're created in Customer.io through their first 90 days. Help me map every campaign they could be enrolled in, every message they'd receive, and the timing of each touchpoint. As we build the map, flag: gaps where there are long periods of silence that might represent a missed opportunity; overlaps where multiple messages land on the same day or compete with each other; redundancies where different campaigns are saying similar things; and moments where the messaging doesn't match where someone is in their journey. Then give me a prioritized set of changes — what to add, what to remove, and what to resequence. Ask me if there are specific segments or customer types I want to trace through the journey before we start.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },
  {
    title: "Find contacts who should be in a campaign but aren't",
    description: "Cross-reference your segments and campaign enrollment to find who's slipping through.",
    prompt:
      "I want to audit who's falling through the cracks in my campaign coverage. Help me identify contacts who meet the criteria for a campaign but aren't enrolled — whether because they joined a segment after a campaign started, because their attributes qualify them for something but no trigger ever fired, or because there's simply no campaign targeting their situation. Also look for segments that have no associated campaign at all — audiences I've defined but never actually messaged. For each gap you find, tell me why it's happening and what I should do about it: fix the trigger logic, build a new campaign, or backfill the audience. I want to come out of this with confidence that the right people are getting the right messages — and a clear list of what's broken or missing.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3 && context.counts.segmentsCount >= 3;
    },
  },
  {
    title: 'Diagnose a segment size issue',
    description: "Something's off with a segment count — figure out why and fix the conditions.",
    prompt:
      "I have a segment that doesn't look right — the count is way off from what I'd expect and I'm not sure why. Ask me which segment it is and what I expected it to contain. Then help me systematically diagnose what's wrong: walk through each condition and whether it's filtering correctly, check for logic errors between AND/OR operators, look for data quality issues like missing attributes or inconsistent event names, and identify whether the problem is in the definition or in the underlying data. Tell me exactly what to change to get it to the right population, and flag anything in the data model that might cause similar issues elsewhere.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.segmentsCount >= 3;
    },
  },
  {
    title: 'Identify contacts being over-messaged',
    description: 'Find people enrolled in too many campaigns simultaneously and build a suppression strategy.',
    prompt:
      "I'm worried that some of my contacts are being hit by too many campaigns at once and it's hurting engagement or causing fatigue. Help me figure out who's over-messaged — ask me what an acceptable number of messages per week looks like for my audience, then help me identify the segments and campaign combinations most likely to pile up. Look for contacts enrolled in multiple active campaigns with no frequency cap or suppression logic in place. Then help me build a suppression strategy: whether that's a global frequency cap, a suppression segment, or adjustments to campaign entry conditions. I want guardrails so this doesn't keep happening as I add more campaigns.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },
  {
    title: 'Reverse-engineer your best campaign',
    description: 'Take your top performer, understand exactly why it works, and apply that logic elsewhere.',
    prompt:
      "I want to figure out exactly why my best campaign performs so well — and then apply that thinking to the rest of my account. Ask me which campaign I consider my top performer and what metric I'm using to judge it. Then help me break down every element that might be contributing: the trigger timing, the audience conditions, the message content, the subject line, the CTA, the send cadence, and how it's structured relative to other campaigns. Build a hypothesis for what's actually driving the results. Then help me identify 2–3 other campaigns that could be improved by applying the same logic, and walk me through what to change in each one.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 5;
    },
  },

  // ── Merged cards (formerly duplicated across tiers) ─────────────────

  {
    title: 'Build a survey',
    description: 'Collect feedback from customers with an in-app survey experience.',
    prompt:
      "I want to build a survey to collect feedback from my customers using in-app messages. Ask me what I'm trying to learn — NPS, feature feedback, onboarding satisfaction, or something else. Help me design the survey flow: the questions, the in-app message format, and how to capture and use the responses. Walk me through setting it up as a campaign so it targets the right audience at the right moment.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.enabledActions.includes('in_app') && context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Set up LLM Actions',
    description: 'Use AI to generate dynamic content or make decisions inside your workflows.',
    prompt:
      'I want to use LLM Actions in my campaigns to generate dynamic content or make smart decisions. Explain what LLM Actions can do — like personalizing messages with AI-generated text, classifying customer intent, or summarizing data. Then help me set up my first LLM Action: pick a use case, configure the prompt, and wire it into a campaign workflow. Walk me through testing it and monitoring the output quality.',
    match: (context) =>
      context.featureFlags['llm-action'] === true && (context.counts == null || context.counts.campaignsCount >= 2),
  },
  {
    title: 'Sync to ad platform',
    description: 'Push segments to ad platforms for retargeting or suppression.',
    prompt:
      "I want to sync my Customer.io segments to an ad platform for retargeting or suppression. Ask me which platform I'm using — Google Ads, Facebook, or another — and what my goal is. Walk me through setting up the integration, choosing which segments to sync, and configuring the sync frequency. Help me think through suppression lists so I'm not wasting ad spend on people I'm already reaching through email.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.segmentsCount >= 3;
    },
  },
  {
    title: 'Automate internal process',
    description: 'Trigger internal notifications or actions based on customer behavior.',
    prompt:
      'I want to use Customer.io to automate an internal process — like notifying my sales team when a lead does something important, updating a CRM when a customer reaches a milestone, or triggering a webhook to another internal tool. Ask me what process I want to automate and what triggers it. Then help me build the workflow: the trigger event, any conditions or filters, and the action (webhook, Slack notification, etc.). Walk me through testing it end-to-end.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2 && context.integrationCompletedAt != null;
    },
  },
  {
    title: 'Upsell or cross-sell',
    description: 'Identify customers ready for an upgrade or complementary product.',
    prompt:
      'I want to build a campaign that upsells or cross-sells to existing customers. Ask me about my product lineup — what plans, tiers, or add-ons I offer. Help me define the signals that indicate a customer is ready for an upgrade or would benefit from a complementary product. Then walk me through building the campaign: the trigger conditions, the message strategy, and how to measure success. Suggest ways to personalize the offer based on customer data.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },
  {
    title: 'Set up a database sync',
    description: 'Keep your data warehouse or external database in sync with Customer.io.',
    prompt:
      "I want to set up a data sync between Customer.io and my data warehouse or external database. Ask me what system I'm syncing with and what data I need to flow in each direction. Walk me through the integration options — direct API, webhook-based sync, or a third-party connector — and help me configure the one that fits best. Cover how to handle schema changes, data conflicts, and monitoring the sync health.",
    match: (context) => context.integrationCompletedAt != null,
  },

  // ── Subscription & Compliance ───────────────────────────────────────

  {
    title: 'Set up a subscription center',
    description: 'Let contacts choose what they hear about instead of unsubscribing from everything.',
    prompt:
      'I want to set up a subscription center so my contacts can manage their preferences instead of fully unsubscribing. Walk me through creating subscription topics, adding the preference center to my emails, and configuring how unsubscribes are handled. Ask me about the types of content I send so you can recommend the right topic structure. Help me think through the migration plan if I already have existing subscribers.',
    match: (context) => !context.subscriptionCenterEnabled,
  },
  {
    title: 'Set up multi-language messaging',
    description: "Send messages in your customers' preferred language.",
    prompt:
      "I want to send messages in multiple languages based on my customers' preferences. Walk me through setting up the language attribute, creating translated versions of my messages, and configuring fallback behavior for contacts without a language set. Ask me which languages I need to support and how I'm currently tracking language preferences. Help me design a workflow that scales as I add more languages.",
    match: (context) => isEmptyString(context.languageAttribute),
  },

  // ── Collections & Snippets ──────────────────────────────────────────

  {
    title: 'Create a dynamic content collection',
    description: 'Pull in product catalogs, blog posts, or recommendations from external data.',
    prompt:
      "I want to use Collections in Customer.io to pull in external data — like a product catalog, blog feed, or recommendation list — and use it in my messages. Ask me what data source I'm working with and how I want to use it in my campaigns. Walk me through creating the collection, connecting it to my data, and using liquid to render dynamic content in my emails. Help me think about caching, refresh intervals, and fallback content.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Create reusable snippets',
    description: 'Build shared content blocks you can use across campaigns.',
    prompt:
      "I want to create reusable snippets — shared content blocks I can use across multiple campaigns and broadcasts. Ask me what kind of content I'm repeating (headers, footers, product blocks, legal disclaimers, etc.) and help me design snippets that are flexible enough to reuse. Walk me through creating them, inserting them in messages, and managing updates so changes propagate everywhere automatically.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 3;
    },
  },

  // ── Relationship & Object Campaigns ─────────────────────────────────

  {
    title: 'Build a relationship-triggered campaign',
    description: 'Trigger messages based on changes to related objects like accounts or orders.',
    prompt:
      'I want to build a campaign that triggers based on changes to a related object — like when an account upgrades, an order ships, or a subscription renews. Ask me about my object model and what relationship change I want to react to. Walk me through setting up the trigger, accessing related object data in the message with liquid, and handling edge cases like multiple relationships or missing data.',
    match: (context) => context.objectTypesCount >= 1,
  },
  {
    title: 'Build an object-triggered campaign',
    description: 'Send messages when objects are created, updated, or match specific conditions.',
    prompt:
      "I want to build a campaign that triggers based on object events — like when a new order is created, a support ticket is updated, or a subscription reaches a certain state. Ask me what object type I'm working with and what event or condition should trigger the message. Walk me through the campaign setup, accessing object attributes in the message, and targeting the right person or people associated with the object.",
    match: (context) => context.objectTypesCount >= 1,
  },

  // ── Data & Integration ──────────────────────────────────────────────

  {
    title: 'Set up an export',
    description: 'Export customer data, events, or campaign metrics for analysis.',
    prompt:
      "I want to export data from Customer.io for analysis or reporting. Ask me what I'm trying to analyze — customer attributes, event data, campaign performance, or deliverability metrics. Walk me through setting up the right export type, configuring filters and date ranges, and choosing the format. Help me understand what data is available and suggest the best approach for my analysis goal.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 1;
    },
  },
  {
    title: 'Clean up your event data',
    description: 'Find redundant, misspelled, or unused events and clean them up.',
    prompt:
      "I want to audit and clean up my event data in Customer.io. Help me identify events that are redundant, misspelled, or no longer being sent. Look for events that aren't used in any campaign trigger or segment condition. Then help me plan the cleanup: which events to keep, which to deprecate, and how to update any campaigns or segments that reference the ones I'm removing. Ask me about my product first so you understand what events matter.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.eventsCount >= 10;
    },
  },

  // ── Webhook & Form Campaigns ────────────────────────────────────────

  {
    title: 'Build a webhook-triggered campaign',
    description: 'React to events from external systems via incoming webhooks.',
    prompt:
      'I want to build a campaign that triggers when Customer.io receives a webhook from an external system — like a payment processor, support tool, or internal service. Ask me what system will send the webhook and what event it represents. Walk me through setting up the webhook endpoint, mapping the payload data, configuring the campaign trigger, and testing the full flow end-to-end.',
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 2;
    },
  },
  {
    title: 'Build a form-triggered campaign',
    description: 'React to form submissions from your website or app.',
    prompt:
      "I want to create a campaign that triggers when someone submits a form — like a contact form, lead magnet signup, or feedback form. Ask me about the form and what action I want to take when it's submitted. Walk me through connecting the form data to Customer.io, setting up the trigger, and building the follow-up message or workflow. Help me think through data mapping and validation.",
    match: (context) => {
      if (context.counts == null) return true;
      return context.counts.campaignsCount >= 1;
    },
  },
];

/**
 * Returns prompt cards relevant to the workspace, shuffled deterministically per day.
 *
 * 1. Run each card's `match(ctx)` against the workspace context
 * 2. Deterministic daily shuffle of matched cards
 * 3. Return top `count` cards
 * 4. If fewer than `count` match, fill from the full pool via shuffle
 */
export function getPromptsForWorkspace(context: WorkspaceContext, count = 4): PromptCard[] {
  const matched = prompts.filter((p) => p.match(context));

  const now = new Date();
  const daySeed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();

  const shuffled = [...matched].sort((a, b) => {
    const hashA = simpleHash(`${daySeed}-${a.title}`);
    const hashB = simpleHash(`${daySeed}-${b.title}`);
    return hashA - hashB;
  });

  if (shuffled.length >= count) {
    return shuffled.slice(0, count);
  }

  // Fill from unmatched cards if needed
  const matchedTitles = new Set(matched.map((p) => p.title));
  const unmatched = prompts.filter((p) => !matchedTitles.has(p.title));
  const shuffledFill = [...unmatched].sort((a, b) => {
    const hashA = simpleHash(`${daySeed}-${a.title}`);
    const hashB = simpleHash(`${daySeed}-${b.title}`);
    return hashA - hashB;
  });

  return [...shuffled, ...shuffledFill].slice(0, count);
}

function simpleHash(string_: string): number {
  let hash = 0;
  for (let index = 0; index < string_.length; index++) {
    const char = string_.charCodeAt(index);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash;
}
